/**
 * Quick Action Card Component
 * 
 * Displays a single contextual action card with icon, title, and description.
 * Uses TeeLogix theme colors for consistent styling.
 * Compact horizontal layout for vertical stacking.
 */
import { LucideIcon, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

export interface QuickAction {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  prompt: string; // The message to send when clicked
}

interface QuickActionCardProps {
  action: QuickAction;
  onClick: (action: QuickAction) => void;
  isLoading?: boolean;
}

export const QuickActionCard = ({
  action,
  onClick,
  isLoading = false,
}: QuickActionCardProps) => {
  const Icon = action.icon;

  return (
    <button
      onClick={() => onClick(action)}
      disabled={isLoading}
      className={cn(
        "w-full px-3 py-2.5 rounded-xl border border-gray-200/60 bg-white/50 cursor-pointer text-left relative overflow-hidden group",
        "hover:border-emerald-200 hover:shadow-md hover:bg-white hover:-translate-y-0.5 transition-all duration-300 ease-out",
        "focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:ring-offset-1",
        "disabled:opacity-50 disabled:cursor-not-allowed"
      )}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-emerald-50/0 via-emerald-50/30 to-emerald-50/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="flex items-center gap-3 relative z-10">
        <div
          className={cn(
            "h-8 w-8 rounded-lg border border-gray-100 bg-white shadow-sm flex items-center justify-center flex-shrink-0",
            "group-hover:border-emerald-100 group-hover:bg-emerald-50 group-hover:text-emerald-600 transition-colors duration-300"
          )}
        >
          <Icon
            className={cn(
              "h-4 w-4 text-gray-400",
              "group-hover:text-emerald-600 transition-colors duration-300"
            )}
          />
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="font-semibold text-gray-700 text-xs leading-tight mb-0.5 group-hover:text-emerald-700 transition-colors">
            {action.title}
          </h4>
          <p className="text-gray-400 text-[10px] leading-tight truncate group-hover:text-gray-500 transition-colors">
            {action.description}
          </p>
        </div>
        <ChevronRight className="h-3.5 w-3.5 text-gray-300 group-hover:text-emerald-400 group-hover:translate-x-0.5 transition-all duration-300 flex-shrink-0" />
      </div>
    </button>
  );
};

